import Environment, { IS_LOCALHOST } from '@omni/kit/Environment';
import React, {
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useEffect,
} from 'react';

const featuresConfig = {
  endUserAuth: {
    isEnterprise: true,
    siteKey: IS_LOCALHOST
      ? Environment.euaRecaptchaSiteKeyTestSuccessWeb
      : Environment.euaRecaptchaSiteKey,
  },
  eventPayment: {
    isEnterprise: false,
    siteKey: Environment.googleCaptchaKey,
  },
  giving: {
    isEnterprise: false,
    siteKey: Environment.googleCaptchaKey,
  },
};

type Feature = keyof typeof featuresConfig;

interface ExecuteOptions {
  action: string;
}

interface Grecaptcha {
  ready(callback: () => void): void;
  execute(siteKey: string, options: ExecuteOptions): Promise<string>;
  enterprise: {
    ready(callback: () => void): void;
    execute(siteKey: string, options: ExecuteOptions): Promise<string>;
  };
}

declare global {
  interface Window {
    grecaptcha: Grecaptcha;
  }
}

interface GoogleCaptchaContextType {
  fetchCaptcha: (action: string) => Promise<string | undefined>;
  hideBadge?: boolean;
  siteKey?: string;
}

interface ProviderProps {
  children?: ReactNode;
  feature: Feature;

  /**
   * Set hideBadge to true to hide the Google reCAPTCHA badge, which appears on the screen when displaying privacy policy and terms links in the page
   * For requirements when hiding the badge, see
   * https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed
   */
  hideBadge?: boolean;
}

const GoogleCaptchaContext = createContext<
  GoogleCaptchaContextType | undefined
>(undefined);

export const GoogleCaptchaProvider = ({
  children,
  hideBadge,
  feature,
}: ProviderProps): null | ReactElement => {
  const config = featuresConfig[feature];

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    // Safety check for SSR - only proceed if Environment is loaded
    if (!config.siteKey) {
      return;
    }

    const script = document.createElement('script');
    script.src = config.isEnterprise
      ? `https://www.google.com/recaptcha/enterprise.js?render=${config.siteKey}`
      : `https://www.google.com/recaptcha/api.js?render=${config.siteKey}`;

    document.body.appendChild(script);

    if (hideBadge) {
      const style = document.createElement('style');
      style.innerHTML = '.grecaptcha-badge { visibility: hidden; }';
      document.head.appendChild(style);
    }

    return () => {
      document.body.removeChild(script);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCaptcha = async (action: string) => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    // Safety check for SSR - only proceed if Environment is loaded
    if (!config.siteKey) {
      return undefined;
    }

    if (config.isEnterprise) {
      if (window.grecaptcha?.enterprise?.execute) {
        return await window.grecaptcha.enterprise.execute(config.siteKey, {
          action,
        });
      }

      return undefined;
    } else {
      if (window.grecaptcha?.execute) {
        return await window.grecaptcha.execute(config.siteKey, {
          action,
        });
      }

      return undefined;
    }
  };

  return (
    <GoogleCaptchaContext.Provider
      value={{ hideBadge, fetchCaptcha, siteKey: config.siteKey }}
    >
      {children}
    </GoogleCaptchaContext.Provider>
  );
};

export const useCaptcha = (): GoogleCaptchaContextType => {
  const context = useContext(GoogleCaptchaContext);

  if (context === undefined) {
    throw new Error(
      'useCaptcha must be used within a component wrapped with GoogleCaptchaProvider'
    );
  }

  return context;
};
